import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkles, Send, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Universe-specific races and classes
const UNIVERSE_DATA = {
  starwars: {
    name: 'Star Wars',
    races: ['Human', 'Wookiee', 'Twi\'lek', 'Rodian', 'Zabrak', 'Togruta', 'Chiss', 'Mandalorian', 'Other (specify)'],
    classes: ['Jedi', 'Sith', 'Smuggler', 'Bounty Hunter', 'Pilot', 'Soldier', 'Engineer', 'Diplomat']
  },
  got: {
    name: 'Game of Thrones',
    races: ['Westerosi', 'Dothraki', 'Valyrian', 'First Men', 'Andal', 'Wildling', 'Ironborn', 'Dornish', 'Other (specify)'],
    classes: ['Knight', 'Lord/Lady', 'Assassin', 'Maester', 'Sellsword', 'Ranger', 'Noble', 'Commoner']
  },
  lotr: {
    name: 'Lord of the Rings',
    races: ['Human', 'Elf', 'Dwarf', 'Hobbit', 'Wizard (Istari)', 'Ranger', 'Dunedain', 'Other (specify)'],
    classes: ['Warrior', 'Ranger', 'Wizard', 'Rogue', 'Scholar', 'Craftsman', 'Bard', 'Hunter']
  },
  warhammer40k: {
    name: 'Warhammer 40K',
    races: ['Human (Imperial)', 'Space Marine', 'Tech-Priest', 'Eldar', 'Ork', 'Tau', 'Psyker', 'Other (specify)'],
    classes: ['Space Marine', 'Imperial Guard', 'Inquisitor', 'Tech-Priest', 'Rogue Trader', 'Assassin', 'Psyker', 'Commissar']
  }
};

export default function CharacterCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  
  // World context from dashboard navigation
  const worldId = location.state?.worldId || null;
  const preselectedUniverse = location.state?.universe || '';
  const preselectedTimeline = location.state?.timelinePosition || '';
  // Canon launch context (from CanonLibraryModal)
  const canonBookId = location.state?.canonBookId || null;
  const canonStartBookIndex = location.state?.canonStartBookIndex ?? 0;
  const canonStartChapterIndex = location.state?.canonStartChapterIndex ?? 0;
  const canonBookTitle = location.state?.canonBookTitle || '';
  const canonSeriesName = location.state?.canonSeriesName || '';
  const canonWorldName = location.state?.canonWorldName || '';
  const canonWorldWasCreated = location.state?.canonWorldWasCreated || false;
  const [mode, setMode] = useState(null); // null = choice screen, 'manual' = form, 'chat' = talk to GM
  const [loading, setLoading] = useState(false);
  const [showOtherRace, setShowOtherRace] = useState(false);
  const [customRace, setCustomRace] = useState('');
  const [customUniverse, setCustomUniverse] = useState('');
  // Chat mode state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [creatingCharacter, setCreatingCharacter] = useState(false);
  const chatEndRef = React.useRef(null);
  
  const [formData, setFormData] = useState({
    universe: preselectedUniverse,
    name: '',
    race: '',
    char_class: '',
    backstory: '',
    physical_description: '',
    starting_location: '',
    gm_rules: '',
    starting_inventory: '',
    ruleset: 'freeform',
    generate_portrait: true,
    image_model: 'openai',
    sheet_creator: 'gm',
    story_bible_description: canonBookTitle || '',
  });

  const [rulesets, setRulesets] = useState([]);

  useEffect(() => {
    axios.get(`${API}/rulesets`).then(res => {
      setRulesets(res.data);
    }).catch(() => {});
  }, []);

  // Start chat with GM greeting
  const startChatMode = async () => {
    setMode('chat');
    setChatLoading(true);
    try {
      const response = await axios.post(`${API}/characters/chat-create`, { messages: [] }, { withCredentials: true });
      setChatMessages([{ role: 'assistant', content: response.data.reply }]);
    } catch (error) {
      toast.error('Failed to start chat. Try again.');
      setMode(null);
    } finally {
      setChatLoading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading || creatingCharacter) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    const newMessages = [...chatMessages, { role: 'user', content: userMsg }];
    setChatMessages(newMessages);
    setChatLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/characters/chat-create`,
        { messages: newMessages },
        { withCredentials: true }
      );
      
      setChatMessages([...newMessages, { role: 'assistant', content: response.data.reply }]);
      
      if (response.data.is_complete && response.data.character_data && !creatingCharacter) {
        // Character info gathered — create it (guard against duplicates)
        setCreatingCharacter(true);
        const charData = response.data.character_data;
        setTimeout(async () => {
          try {
            setLoading(true);
            const createRes = await axios.post(
              `${API}/characters`,
              {
                universe: charData.universe || 'dnd',
                name: charData.name || 'Unknown Hero',
                race: charData.race || 'Human',
                char_class: charData.char_class || 'Adventurer',
                physical_description: charData.physical_description || '',
                backstory: charData.backstory || '',
                starting_location: charData.starting_location || '',
                gm_rules: charData.gm_rules || '',
                starting_inventory: charData.starting_inventory || '',
                ruleset: charData.ruleset || 'freeform',
                generate_portrait: true,
                image_model: 'openai',
                sheet_creator: 'gm',
              },
              { withCredentials: true }
            );
            toast.success('Character created!');
            const campaignRes = await axios.post(
              `${API}/campaigns?character_id=${createRes.data.character_id}`,
              {},
              { withCredentials: true }
            );
            navigate(`/game/${createRes.data.character_id}`);
          } catch (error) {
            toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to create character');
            setCreatingCharacter(false);
          } finally {
            setLoading(false);
          }
        }, 1500);
      }
    } catch (error) {
      toast.error('Failed to get response. Try again.');
    } finally {
      setChatLoading(false);
    }
  };

  React.useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const isCustomUniverse = formData.universe === 'other';
  const selectedUniverse = formData.universe && !isCustomUniverse ? UNIVERSE_DATA[formData.universe] : null;

  const handleRaceChange = (value) => {
    if (value === 'Other (specify)') {
      setShowOtherRace(true);
      setFormData({ ...formData, race: '' });
    } else {
      setShowOtherRace(false);
      setCustomRace('');
      setFormData({ ...formData, race: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const finalRace = showOtherRace ? customRace : formData.race;
    const finalUniverse = isCustomUniverse ? customUniverse : formData.universe;
    
    if ((!formData.universe) || (isCustomUniverse && !customUniverse) || !formData.name || !finalRace || !formData.char_class) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/characters`,
        { ...formData, race: finalRace, universe: finalUniverse, world_id: worldId },
        { withCredentials: true }
      );

      toast.success('Character created successfully!');
      
      // Start a new campaign with this character. If launched from a canon
      // book, pass the canon params so the bible is seeded directly at the
      // chosen position (instant, zero LLM cost).
      const campaignBody = canonBookId
        ? {
            canon_book_id: canonBookId,
            canon_start_book_index: canonStartBookIndex,
            canon_start_chapter_index: canonStartChapterIndex,
          }
        : {};
      const campaignRes = await axios.post(
        `${API}/campaigns?character_id=${response.data.character_id}`,
        campaignBody,
        { withCredentials: true }
      );

      navigate(`/game/${response.data.character_id}`, {
        state: { campaignId: campaignRes.data.campaign_id }
      });
    } catch (error) {
      console.error('Character creation failed:', error);
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to create character');
    } finally {
      setLoading(false);
    }
  };

  // ===================== CHOICE SCREEN =====================
  if (mode === null) {
    return (
      <div className="min-h-screen py-8 sm:py-12 px-4 sm:px-6" data-testid="character-create-choice">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-blue-500/50 hover:text-blue-400 mb-8"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {canonBookId && (
              <div className="mb-6 p-4 rounded-lg border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-transparent" data-testid="canon-launch-banner">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="h-5 w-5 text-amber-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-amber-400/70 uppercase tracking-wider">Launching canonical campaign</p>
                    <p className="text-sm font-bold text-amber-200 font-mono truncate">{canonBookTitle}</p>
                    {canonSeriesName && <p className="text-xs text-slate-400 truncate">{canonSeriesName}</p>}
                    <p className="text-[10px] text-slate-500 mt-1">
                      World: <span className="text-slate-300">{canonWorldName}</span>{canonWorldWasCreated ? ' (newly created)' : ' (reusing existing)'}
                      {(canonStartBookIndex > 0 || canonStartChapterIndex > 0) && (
                        <> · Starting at Book {canonStartBookIndex + 1}, Chapter {canonStartChapterIndex + 1}</>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <h1 className="text-4xl font-bold text-blue-400 font-mono mb-2">Create Your Character</h1>
            <p className="text-slate-400 mb-8">Choose how you'd like to bring your character to life.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Manual Creation */}
              <button
                onClick={() => setMode('manual')}
                className="group glass-card rounded-2xl overflow-hidden border-2 border-white/5 hover:border-blue-400/40 transition-all text-left"
                data-testid="choose-manual"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={preselectedUniverse === 'starwars'
                      ? '/char-create-sw-manual.png'
                      : 'https://static.prod-images.emergentagent.com/jobs/0b2b8cb6-a3b2-4978-9089-22546daab7b8/images/74e744b784ca29d5d956f8e4de1c46feaafd1c0d764ee92fe4e6fe45c6925c0f.png'}
                    alt="Manual creation"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h2 className="text-xl font-bold text-slate-200 mb-1 font-mono">Manual Creation</h2>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Fill out your character's details — universe, race, class, backstory, and more. The GM will generate your character sheet and stats.
                  </p>
                </div>
              </button>

              {/* Talk to GM */}
              <button
                onClick={startChatMode}
                className="group glass-card rounded-2xl overflow-hidden border-2 border-white/5 hover:border-amber-400/40 transition-all text-left"
                data-testid="choose-chat"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={preselectedUniverse === 'starwars'
                      ? '/char-create-sw-talktogm.png'
                      : 'https://static.prod-images.emergentagent.com/jobs/0b2b8cb6-a3b2-4978-9089-22546daab7b8/images/4eddcd248723f7da8c6b0848cd431966c4c4352d465878c3a6f8eb4fb76fbdf4.png'}
                    alt="Talk to GM"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h2 className="text-xl font-bold text-slate-200 mb-1 font-mono">Talk to the GM</h2>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Have a conversation with your Game Master. They'll guide you through character creation naturally — just answer their questions.
                  </p>
                </div>
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // ===================== CHAT MODE =====================
  if (mode === 'chat') {
    return (
      <div className="min-h-screen py-8 sm:py-12 px-4 sm:px-6" data-testid="character-create-chat">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => { setMode(null); setChatMessages([]); }}
            className="text-blue-500/50 hover:text-blue-400 mb-8"
            data-testid="back-to-choice"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-2xl overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 180px)' }}>
            {/* Header */}
            <div className="p-5 border-b border-white/5">
              <h1 className="text-2xl font-bold text-amber-400 font-mono">Speak with the Game Master</h1>
              <p className="text-slate-500 text-sm">Your GM will guide you through character creation.</p>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {chatMessages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] rounded-2xl p-4 ${
                    msg.role === 'user'
                      ? 'bg-blue-400/10 border border-blue-400/20 text-slate-200 rounded-br-none'
                      : 'bg-slate-800/50 border border-amber-500/10 text-slate-300 rounded-bl-none'
                  }`}>
                    <p className="whitespace-pre-wrap leading-relaxed text-sm">{msg.content}</p>
                  </div>
                </motion.div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-800/50 border border-amber-500/10 rounded-2xl rounded-bl-none p-4">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 bg-amber-400/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-amber-400/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-amber-400/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              {loading && (
                <div className="flex justify-center py-4">
                  <div className="flex items-center gap-3 text-amber-400">
                    <Sparkles className="h-5 w-5 animate-pulse" />
                    <span className="font-mono text-sm">Creating your character...</span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/5">
              <div className="flex gap-2">
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                  placeholder="Type your response..."
                  className="flex-1 bg-slate-950/50 border border-white/10 rounded-lg px-4 py-2.5 text-slate-200 text-sm focus:outline-none focus:border-amber-400/50"
                  disabled={chatLoading || loading}
                  data-testid="chat-create-input"
                />
                <Button
                  onClick={sendChatMessage}
                  disabled={chatLoading || loading || !chatInput.trim()}
                  className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30"
                  data-testid="chat-create-send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // ===================== MANUAL MODE =====================
  return (
    <div className="min-h-screen py-8 sm:py-12 px-4 sm:px-6" data-testid="character-create">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => setMode(null)}
          className="text-blue-500/50 hover:text-blue-400 mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 rounded-2xl"
        >
          <h1 className="text-4xl font-bold text-blue-400 font-mono mb-2">
            {t('character.create')}
          </h1>
          <p className="text-slate-400 mb-8">
            Forge your legend. Create a unique character for your adventures.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Universe Selection — hidden when creating from a world */}
            {!worldId && (
            <>
            <div>
              <Label htmlFor="universe" className="text-slate-300 mb-2 block">
                Universe *
              </Label>
              <Select
                value={formData.universe}
                onValueChange={(value) => setFormData({ ...formData, universe: value, race: '', char_class: '' })}
              >
                <SelectTrigger
                  className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200"
                  data-testid="universe-select"
                >
                  <SelectValue placeholder="Select universe" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {Object.entries(UNIVERSE_DATA).map(([key, data]) => (
                    <SelectItem
                      key={key}
                      value={key}
                      className="text-slate-200 focus:bg-blue-500/20 focus:text-blue-400"
                    >
                      {data.name}
                    </SelectItem>
                  ))}
                  <SelectItem
                    value="other"
                    className="text-slate-200 focus:bg-blue-500/20 focus:text-blue-400"
                  >
                    Other (Custom Universe)
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-slate-500 text-xs mt-1">Choose the world your story takes place in</p>
            </div>

            {isCustomUniverse && (
              <div>
                <Label className="text-slate-300 mb-2 block">Universe Name *</Label>
                <Input
                  value={customUniverse}
                  onChange={(e) => setCustomUniverse(e.target.value)}
                  placeholder="e.g., My Homebrew World, Cyberpunk 2077, Naruto..."
                  className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200"
                  data-testid="custom-universe-input"
                  required
                />
                <p className="text-slate-500 text-xs mt-1">Name your universe — the AI will adapt to any setting you describe</p>
              </div>
            )}
            </>
            )}

            {/* Ruleset Selection — always visible (in or out of a world) */}
            <div className="glass-card p-6 rounded-lg border border-white/5">
              <Label className="text-slate-300 mb-2 block font-semibold">
                Game Ruleset *
              </Label>
              <p className="text-slate-500 text-xs mb-3">
                Choose the rules system for your campaign. Once selected, this <strong className="text-amber-400">cannot be changed</strong> — it's locked for the entire adventure.
              </p>
              <Select
                value={formData.ruleset}
                onValueChange={(value) => setFormData({ ...formData, ruleset: value })}
              >
                <SelectTrigger
                  className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200"
                  data-testid="ruleset-select"
                >
                  <SelectValue placeholder="Select ruleset" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {rulesets.map((rs) => (
                    <SelectItem
                      key={rs.id}
                      value={rs.id}
                      className="text-slate-200 focus:bg-blue-500/20 focus:text-blue-400"
                    >
                      {rs.short}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Ruleset Description */}
              {formData.ruleset && rulesets.length > 0 && (
                <div className="mt-3 p-3 bg-slate-950/40 rounded-lg border border-white/5">
                  <p className="text-amber-400 text-sm font-bold font-mono mb-1">
                    {rulesets.find(r => r.id === formData.ruleset)?.name}
                  </p>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    {rulesets.find(r => r.id === formData.ruleset)?.description}
                  </p>
                </div>
              )}
            </div>

            {/* Character Name */}
            <div>
              <Label htmlFor="name" className="text-slate-300 mb-2 block">
                Character Name *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter character name"
                className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200"
                data-testid="character-name-input"
                required
              />
            </div>

            {/* Race */}
            {(selectedUniverse || isCustomUniverse) && (
              <div>
                <Label htmlFor="race" className="text-slate-300 mb-2 block">
                  {selectedUniverse?.name === 'Star Wars' ? 'Species' : 'Race/Origin'} *
                </Label>
                {isCustomUniverse ? (
                  <>
                    <Input
                      value={formData.race}
                      onChange={(e) => setFormData({ ...formData, race: e.target.value })}
                      placeholder="e.g., Elf, Android, Shinobi..."
                      className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200"
                      data-testid="custom-race-input"
                      required
                    />
                    <p className="text-slate-500 text-xs mt-1">Enter any race or species for your universe</p>
                  </>
                ) : (
                  <>
                    <Select
                      value={showOtherRace ? 'Other (specify)' : formData.race}
                      onValueChange={handleRaceChange}
                    >
                      <SelectTrigger
                        className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200"
                        data-testid="race-select"
                      >
                        <SelectValue placeholder="Select race/species" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/10">
                        {selectedUniverse.races.map((race) => (
                          <SelectItem
                            key={race}
                            value={race}
                            className="text-slate-200 focus:bg-blue-500/20 focus:text-blue-400"
                          >
                            {race}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {showOtherRace && (
                      <Input
                        value={customRace}
                        onChange={(e) => setCustomRace(e.target.value)}
                        placeholder="Enter custom race/species name"
                        className="mt-2 bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200"
                        required
                      />
                    )}
                  </>
                )}
              </div>
            )}

            {/* Class */}
            {(selectedUniverse || isCustomUniverse) && (
              <div>
                <Label htmlFor="class" className="text-slate-300 mb-2 block">
                  {isCustomUniverse ? 'Class/Role *' : (selectedUniverse.name === 'Star Wars' ? 'Role' : 'Class/Profession')} *
                </Label>
                {isCustomUniverse ? (
                  <>
                    <Input
                      value={formData.char_class}
                      onChange={(e) => setFormData({ ...formData, char_class: e.target.value })}
                      placeholder="e.g., Mage, Space Pirate, Samurai..."
                      className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200"
                      data-testid="custom-class-input"
                      required
                    />
                    <p className="text-slate-500 text-xs mt-1">Enter any class or profession for your character</p>
                  </>
                ) : (
                  <Select
                    value={formData.char_class}
                    onValueChange={(value) => setFormData({ ...formData, char_class: value })}
                  >
                    <SelectTrigger
                      className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200"
                      data-testid="class-select"
                    >
                      <SelectValue placeholder="Select class/role" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10">
                      {selectedUniverse.classes.map((cls) => (
                        <SelectItem
                          key={cls}
                          value={cls}
                          className="text-slate-200 focus:bg-blue-500/20 focus:text-blue-400"
                        >
                          {cls}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Backstory */}
            <div>
              <Label htmlFor="backstory" className="text-slate-300 mb-2 block">
                {t('character.backstory')}
              </Label>
              <Textarea
                id="backstory"
                value={formData.backstory}
                onChange={(e) => setFormData({ ...formData, backstory: e.target.value })}
                placeholder="Tell us about your character's past..."
                rows={4}
                className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200 resize-none"
                data-testid="backstory-textarea"
              />
            </div>

            {/* Physical Description */}
            <div>
              <Label htmlFor="physical_description" className="text-slate-300 mb-2 block">
                Physical Description (Optional)
              </Label>
              <Textarea
                id="physical_description"
                value={formData.physical_description}
                onChange={(e) => setFormData({ ...formData, physical_description: e.target.value })}
                placeholder="Describe your character's appearance — height, build, hair, eyes, scars, clothing..."
                rows={3}
                className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200 resize-none"
                data-testid="physical-description-textarea"
              />
              <p className="text-slate-500 text-xs mt-1">
                Used to generate your character portrait and helps the GM describe your character in scenes. Example: "Tall and lean with silver hair, piercing green eyes, a jagged scar across the left cheek, wears a dark hooded cloak"
              </p>
            </div>


            {/* Starting Location */}
            <div>
              <Label htmlFor="starting_location" className="text-slate-300 mb-2 block">
                Starting Location
              </Label>
              <Input
                id="starting_location"
                value={formData.starting_location}
                onChange={(e) => setFormData({ ...formData, starting_location: e.target.value })}
                placeholder="Where does your adventure begin?"
                className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200"
                data-testid="starting-location-input"
              />
              <p className="text-slate-500 text-xs mt-1">Example: "A tavern in the kingdom of Aldor"</p>
            </div>

            {/* GM Rules */}
            <div>
              <Label htmlFor="gm_rules" className="text-slate-300 mb-2 block">
                GM Rules & Instructions (Optional)
              </Label>
              <Textarea
                id="gm_rules"
                value={formData.gm_rules}
                onChange={(e) => setFormData({ ...formData, gm_rules: e.target.value })}
                placeholder="Tell the GM what you want (these become absolute rules)..."
                rows={3}
                className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200 resize-none"
                data-testid="gm-rules-textarea"
              />
              <p className="text-slate-500 text-xs mt-1">
                Example: "Always give me choices", "Keep combat challenging", "Focus on roleplay"
              </p>
            </div>

            {/* Story Bible Reference */}
            <div>
              <Label htmlFor="story_bible" className="text-slate-300 mb-2 block">
                Story Bible Reference (Optional)
              </Label>
              <Textarea
                id="story_bible"
                value={formData.story_bible_description}
                onChange={(e) => setFormData({ ...formData, story_bible_description: e.target.value })}
                placeholder="Describe books, series, or storylines you want the GM to follow..."
                rows={3}
                className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200 resize-none"
                data-testid="story-bible-textarea"
              />
              <p className="text-slate-500 text-xs mt-1">
                Example: "Republic Commando series by Karen Traviss — start at True Colors chapter 3" or "Horus Heresy, first trilogy"
              </p>
              <p className="text-emerald-400/60 text-xs mt-1">
                A Story Bible will be auto-generated when the campaign starts, giving the GM chapter-by-chapter canonical reference.
              </p>
            </div>

            {/* Starting Inventory */}
            <div>
              <Label htmlFor="starting_inventory" className="text-slate-300 mb-2 block">
                Starting Inventory (Optional)
              </Label>
              <Textarea
                id="starting_inventory"
                value={formData.starting_inventory}
                onChange={(e) => setFormData({ ...formData, starting_inventory: e.target.value })}
                placeholder="List items your character starts with, separated by commas..."
                rows={3}
                className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200 resize-none"
                data-testid="starting-inventory-textarea"
              />
              <p className="text-slate-500 text-xs mt-1">
                Example: "Iron sword, leather armor, healing potion, 50 gold coins, backpack"
              </p>
            </div>

            {/* Character Sheet Info */}
            <div className="glass-card p-5 rounded-lg border border-amber-500/15 bg-amber-500/5">
              <p className="text-amber-400 text-sm font-semibold mb-1">Character Sheet</p>
              <p className="text-slate-400 text-sm">
                Your character sheet and stats will be generated by the GM based on your race, class, backstory, and chosen ruleset. You can edit your sheet in-game anytime.
              </p>
            </div>


            {/* Portrait Options */}
            <div className="glass-card p-6 rounded-lg border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="text-slate-300 font-semibold">
                    {t('character.generate_portrait')}
                  </Label>
                  <p className="text-slate-500 text-sm mt-1">
                    Generate an AI portrait for your character
                  </p>
                </div>
                <Switch
                  checked={formData.generate_portrait}
                  onCheckedChange={(checked) => setFormData({ ...formData, generate_portrait: checked })}
                  data-testid="portrait-toggle"
                />
              </div>

              {formData.generate_portrait && (
                <div>
                  <Label htmlFor="imageModel" className="text-slate-300 mb-2 block text-sm">
                    {t('character.image_model')}
                  </Label>
                  <Select
                    value={formData.image_model}
                    onValueChange={(value) => setFormData({ ...formData, image_model: value })}
                  >
                    <SelectTrigger
                      className="bg-slate-950/50 border-white/10 focus:border-blue-500/50 text-slate-200"
                      data-testid="image-model-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10">
                      <SelectItem value="openai" className="text-slate-200 focus:bg-blue-500/20">
                        OpenAI (GPT Image 1.5)
                      </SelectItem>
                      <SelectItem value="gemini" className="text-slate-200 focus:bg-blue-500/20">
                        Gemini (Nano Banana 2)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-500 hover:bg-blue-400 text-black font-bold uppercase tracking-widest py-6 text-lg rounded-none shadow-[0_0_15px_rgba(96,165,250,0.3)] hover:shadow-[0_0_25px_rgba(96,165,250,0.5)] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed font-mono"
              data-testid="create-submit-btn"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-5 w-5" />
                  {t('character.start_adventure')}
                </>
              )}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
